import type { IContactPerson } from '@condo/domain';
import { MediaFileCategory } from '@condo/domain';
import { downscaleImageFiles } from '@condo/helpers';
import { linkMediaFile } from '@/entities/asset/api/media-files.api';
import { uploadFilesWithSignedUrls } from '@/entities/document/api/file-signed-url.api';
import { saveContact } from '../api/contacts.api';
import { toContactAvatarAssignmentPayload } from './contactHelpers';
import { cropImageFileToSquare } from './cropImageFileToSquare';

export interface UploadContactAvatarParams {
    file: File;
    contact: Pick<IContactPerson, 'contactId' | 'contactType' | 'firstname' | 'lastname'>;
    maxImageSize?: number;
}

/**
 * Upload a contact avatar via signed URL + link, then assign on the contact (Cockpit ContactAvatar parity).
 *
 * - Square-crops client-side so circular avatars on Cockpit external portal are not stretched.
 * - Links without contactId, then PUT /contacts — avoids basecamp setting Firebase photoURL to the
 *   full uncropped image during link (RAM previously passed contactId on link).
 */
export async function uploadContactAvatar({ file, contact, maxImageSize = 1000 }: UploadContactAvatarParams) {
    const squareFile = await cropImageFileToSquare(file, maxImageSize);
    const [downscaledFile] = await downscaleImageFiles([squareFile], maxImageSize);
    if (!downscaledFile) {
        throw new Error('No image to upload');
    }

    const [signedUrl] = await uploadFilesWithSignedUrls([downscaledFile]);

    const { file: mediaFile } = await linkMediaFile({
        bucketFilename: signedUrl.bucketFilename,
        originalFileName: downscaledFile.name,
        category: MediaFileCategory.Avatar,
    });

    if (!mediaFile.mediaFileId) {
        throw new Error('Avatar upload did not return a media file id');
    }

    await saveContact(toContactAvatarAssignmentPayload(contact, mediaFile.mediaFileId));

    return mediaFile;
}
